import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "locationCheckboxes",
    "locationIdsContainer",
    "locationGroupId",
    "parentLocationGroupId",
    "customLocationsSection",
    "offDescription",
    "globalDescription",
    "customLocationsDescription",
  ];

  connect() {
    this.updateVisualStates();
  }

  toggleStatus(event) {
    const mode = event.currentTarget.value;
    this.customLocationsSectionTarget.classList.toggle("hidden", mode !== "custom_locations");
    this.offDescriptionTarget.classList.toggle("hidden", mode !== "off");
    this.globalDescriptionTarget.classList.toggle("hidden", mode !== "global");
    this.customLocationsDescriptionTarget.classList.toggle("hidden", mode !== "custom_locations");
  }

  toggleLocationGroup(event) {
    event.preventDefault();
    const groupElement = event.currentTarget.closest("[data-location-group-id]");
    const locationIds = groupElement.dataset.locationIds.split(",").filter((id) => id.trim());
    const currentLocationIds = this.getSelectedLocationIds();

    const hasAllLocations = locationIds.every((id) => currentLocationIds.has(id));
    if (hasAllLocations) {
      locationIds.forEach((id) => currentLocationIds.delete(id));
    } else {
      locationIds.forEach((id) => currentLocationIds.add(id));
    }
    this.updateHiddenFields(currentLocationIds);
    this.updateVisualStates();
  }

  toggleLocation(event) {
    event.preventDefault();
    event.stopPropagation();
    const locationElement = event.currentTarget.closest("[data-location-id]");
    const locationId = locationElement.dataset.locationId;
    const parentLocationGroupId = locationElement.dataset.parentLocationGroupId;

    const isCurrentlySelected = this.isLocationSelected(locationId);
    this.setLocationSelection(locationId, !isCurrentlySelected);

    const groupElement = this.locationCheckboxesTarget.querySelector(
      `[data-location-group-id="${parentLocationGroupId}"]`
    );
    if (groupElement) {
      const locationIds = groupElement.dataset.locationIds.split(",").filter((id) => id.trim());
      const selectedLocationIds = this.getSelectedLocationIds();
      const allSelected = locationIds.every((id) => selectedLocationIds.has(id));
      const checkbox = groupElement.querySelector('button[role="checkbox"]');
      if (allSelected) {
        this.setCheckboxVisualState(checkbox, true);
      } else {
        this.setCheckboxVisualState(checkbox, false);
      }
    }
    this.updateVisualStates();
  }

  updateVisualStates() {
    const selectedLocationIds = this.getSelectedLocationIds();
    this.updateLocationVisualStates(selectedLocationIds);
    this.updateLocationGroupVisualStates(selectedLocationIds);
  }

  setLocationSelection(locationId, selected) {
    const locationIds = this.getSelectedLocationIds();

    if (selected) {
      locationIds.add(locationId);
    } else {
      locationIds.delete(locationId);
    }
    this.updateHiddenFields(locationIds);
  }

  updateHiddenFields(locationIds) {
    // Clear existing hidden fields
    this.locationIdsContainerTarget.innerHTML = "";

    // Create new hidden fields for each selected location ID
    locationIds.forEach((locationId) => {
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.name = "tenant_entitlements_subscription_addon_feature[location_ids][]";
      hiddenField.value = locationId;
      this.locationIdsContainerTarget.appendChild(hiddenField);
    });
  }

  updateLocationVisualStates(selectedLocationIds) {
    this.locationCheckboxesTarget.querySelectorAll("[data-location-id]").forEach((element) => {
      const locationId = element.dataset.locationId;
      const checkbox = element.querySelector('button[role="checkbox"]');
      this.setCheckboxVisualState(checkbox, selectedLocationIds.has(locationId));
    });
  }

  updateLocationGroupVisualStates(selectedLocationIds) {
    this.locationCheckboxesTarget
      .querySelectorAll("[data-location-group-id]")
      .forEach((groupElement) => {
        const locationIds = groupElement.dataset.locationIds.split(",").filter((id) => id.trim());
        const allSelected = locationIds.every((id) => selectedLocationIds.has(id));

        const checkbox = groupElement.querySelector('button[role="checkbox"]');
        this.setCheckboxVisualState(checkbox, allSelected);
      });
  }

  setCheckboxVisualState(checkbox, checked) {
    const checkmark = checkbox.querySelector("span");
    if (checked) {
      checkmark.classList.remove("hidden");
      checkbox.dataset.state = "checked";
    } else {
      checkmark.classList.add("hidden");
      checkbox.dataset.state = "unchecked";
    }
  }

  getSelectedLocationIds() {
    const hiddenFields = this.locationIdsContainerTarget.querySelectorAll(
      'input[name="tenant_entitlements_subscription_addon_feature[location_ids][]"]'
    );
    return new Set(
      Array.from(hiddenFields)
        .map((field) => field.value)
        .filter((value) => value.trim())
    );
  }

  isLocationSelected(locationId) {
    return this.getSelectedLocationIds().has(locationId);
  }
}
